import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  AuthActionKey,
  AuthMessageCode,
  LoginRequest,
  ResetPasswordRequest,
  SecurityCodePayload,
  SignUpRequest,
  requestPostLogin,
  requestPostResetPassword,
  requestPostSignUp,
  requestPostVerifySecurityCode
} from 'app/api/auth'
import { notificationController } from 'app/controllers/notification-controller'
import { IUser } from 'app/model/user.model'
import {
  getToken,
  refetchToken,
  refetchUserStorage,
  setToken,
  setUserStorage,
} from 'app/service/storage/storage-service'
import { isEqual } from 'lodash'
import { ResponseType } from 'parkway-web-common'
import { getUserInfo } from './profileSlice'
import { setUser } from './userSlice'

export interface AuthSlice {
  token?: string | null
}

const initialState: AuthSlice = {
  token: getToken(),
}

export const doLogin = createAsyncThunk(
  AuthActionKey.AUTH_ACTION_KEY,
  async (loginPayload: LoginRequest, { dispatch }) => {
    const response: ResponseType<IUser> = await requestPostLogin(loginPayload)
    if (isEqual(response.msgcode, AuthMessageCode.AUTH_SUCCESS)) {
      dispatch(setUser(response.data))
      setUserStorage(JSON.stringify(response.data))
      setToken(response?.data?.token)
      dispatch(getUserInfo())
      return response?.data?.token
    } else {
      notificationController.error({ message: response?.message })
      throw response
    }
  },
)

export const doSignUp = createAsyncThunk(
  'auth/doSignUp',
  async (signUpPayload: SignUpRequest) => requestPostSignUp(signUpPayload),
)

export const doResetPassword = createAsyncThunk(
  'auth/doResetPassword',
  async (resetPassPayload: ResetPasswordRequest) =>
    requestPostResetPassword(resetPassPayload),
)

export const doVerifySecurityCode = createAsyncThunk(
  'auth/doVerifySecurityCode',
  async (securityCodePayload: SecurityCodePayload) =>
    requestPostVerifySecurityCode(securityCodePayload),
)

export const doLogout = createAsyncThunk('auth/doLogout', (_, { dispatch }) => {
  refetchToken()
  dispatch(setUser(null))
  refetchUserStorage()
})

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(doLogin.fulfilled, (state, action) => {
      state.token = action.payload
    })
    builder.addCase(doLogout.fulfilled, state => {
      state.token = ''
    })
  },
})

export default authSlice.reducer
